import { InitialalsAvatar } from '@/components/displays/InitialsAvatar';
import PasswordField from '@/components/inputs/PasswordField/PasswordField';
import { generateAvatarColor, getInitials } from '@/utils/avatar';
import { formatName } from '@/utils/name';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { DialogProps } from '@toolpad/core/useDialogs';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ClassData, StudentData } from '../types/student';

export function StudentCreateDialog({
    payload,
    open,
    onClose,
}: DialogProps<{ data?: { classes: ClassData[] } }, (Pick<StudentData, 'familyName' | 'givenName' | 'name' | 'email' | 'studentNumber' | 'class'> & { password: string }) | null>) {
    const { t } = useTranslation();

    // ui
    const [saving, setSaving] = React.useState(false);
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [passwordError, setPasswordError] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
    const [classSelect, setClassSelect] = React.useState('');
    const [classSelectError, setClassSelectError] = React.useState(false);
    const [classSelectErrorMessage, setClassSelectErrorMessage] = React.useState('');

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (emailError || passwordError || classSelectError) {
            return;
        }

        const data = new FormData(event.currentTarget);
        const password = data.get('password') as string;
        const familyName = data.get('last-name') as string;
        const givenName = data.get('first-name') as string;
        const studentNumber = data.get('student-number') as string;
        const email = data.get('email') as string;
        const classId = data.get('class-select') as string;

        setSaving(true);
        await onClose({
            familyName,
            givenName,
            studentNumber,
            email,
            class: {
                id: classId,
                name: payload.data?.classes.find((c) => c.id === classId)?.name,
            },
            name: formatName(familyName, givenName),
            password,
        });
        setSaving(false);
    };

    const validateInputs = () => {
        const email = document.getElementById('email') as HTMLInputElement;
        const password = document.getElementById('password') as HTMLInputElement;

        let isValid = true;

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
            setEmailError(true);
            setEmailErrorMessage(t('common.form.invalidEmail'));
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        if (!password.value || password.value.length < 8) {
            setPasswordError(true);
            setPasswordErrorMessage(t('common.form.invalidPassword'));
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        if (!classSelect) {
            setClassSelectError(true);
            setClassSelectErrorMessage(t('common.form.invalidClass'));
            isValid = false;
        } else {
            setClassSelectError(false);
            setClassSelectErrorMessage('');
        }

        return isValid;
    };

    return (
        <Dialog fullWidth open={open} onClose={() => onClose(null)}>
            <Box component='form' onSubmit={handleSubmit} noValidate>
                <DialogTitle>{t('student.actions.createStudent')}</DialogTitle>
                <DialogContent>
                    <Stack sx={{ pt: 1 }} gap={2}>
                        <Stack direction={'row'} gap={2}>
                            <TextField id='first-name' name='first-name' label={t('student.label.givenName')} fullWidth autoFocus />
                            <TextField id='last-name' name='last-name' label={t('student.label.familyName')} fullWidth />
                        </Stack>
                        <FormControl required error={classSelectError}>
                            <InputLabel id='class-select-label' color={classSelectError ? 'error' : 'primary'}>
                                {t('student.label.class')}
                            </InputLabel>
                            <Select
                                labelId='class-select-label'
                                id='class-select'
                                label={t('student.label.class')}
                                name='class-select'
                                value={classSelect}
                                onChange={(event) => {
                                    setClassSelect(event.target.value);
                                }}
                                required
                                color={classSelectError ? 'error' : 'primary'}
                            >
                                {payload.data?.classes.map((c) => {
                                    const name = c.name || '';
                                    const avatarColor = generateAvatarColor(name);
                                    const bg = avatarColor?.bg;
                                    const text = avatarColor?.text;

                                    return (
                                        <MenuItem key={c.id} value={c.id}>
                                            <Stack direction='row' spacing={1.5} alignItems='center'>
                                                <InitialalsAvatar
                                                    sx={{
                                                        bgcolor: bg,
                                                        color: text,
                                                    }}
                                                >
                                                    {getInitials(name)}
                                                </InitialalsAvatar>
                                                <Typography>{`${c.name} (${c.schoolYear})`}</Typography>
                                            </Stack>
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            {classSelectErrorMessage && <FormHelperText>{classSelectErrorMessage}</FormHelperText>}
                        </FormControl>
                        <TextField id='student-number' name='student-number' label={t('student.label.number')} />
                        <TextField
                            id='email'
                            label={t('student.label.email')}
                            type='email'
                            name='email'
                            error={emailError}
                            helperText={emailErrorMessage}
                            placeholder='student@email.com'
                            color={emailError ? 'error' : 'primary'}
                            required
                            autoComplete='email'
                        />
                        <PasswordField required fullWidth error={passwordError} helperText={passwordErrorMessage} color={passwordError ? 'error' : 'primary'} />
                    </Stack>
                </DialogContent>
                <Stack direction='row' sx={{ justifyContent: 'flex-end', gap: 1, p: 1 }}>
                    <Button
                        onClick={async () => {
                            onClose(null);
                        }}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button loading={saving} variant='contained' type='submit' onClick={validateInputs} loadingIndicator={<CircularProgress size={16} />}>
                        {t('common.save')}
                    </Button>
                </Stack>
            </Box>
        </Dialog>
    );
}
